import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminSubscriptionPlansQuery,
  adminSubscriptionSummaryQuery,
  adminDiscountCouponsQuery,
} from '../../queries/appQueries';
import {
  Text,
  Title,
  Group,
  Stack,
  useMantineColorScheme,
  Box,
  Card,
  SimpleGrid,
  Button,
  Table,
  Modal,
  TextInput,
  NumberInput,
  Textarea,
  Select,
  Switch,
  Menu,
  ActionIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { subscriptionPlanService, SubscriptionPlanDTO, CreateSubscriptionPlanDTO } from '../../services/subscriptionPlanService';
import { discountCouponService, type DiscountCouponDTO, type CreateDiscountCouponDTO, type DiscountType } from '../../services/discountCouponService';
import type { SubscriptionSummaryDTO } from '../../services/subscriptionDashboardService';
import PageTransition from '../../components/animations/PageTransition';
import AdminPageHeader from '../../components/AdminPageHeader';
import {
  IconCoin,
  IconUsers,
  IconPlus,
  IconEdit,
  IconTrash,
  IconGift,
  IconPower,
  IconCircleOff,
  IconSettings,
} from '@tabler/icons-react';

const SubscriptionsPage: React.FC = () => {
  const [planModalOpen, setPlanModalOpen] = useState<boolean>(false);
  const [couponModalOpen, setCouponModalOpen] = useState<boolean>(false);
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  // Paleta sobria unificada con Reactivos/Dashboard.
  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const innerCardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(15, 23, 42, 0.5)' : 'rgba(255, 255, 255, 0.5)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(221, 216, 209, 0.7)'}`,
    boxShadow: 'none',
  };
  const textColor = dark ? '#e2e8f0' : '#2d2a26';
  const mutedColor = dark ? '#94a3b8' : '#8c8478';
  const colActiva = dark ? '#86b8a6' : '#4f8a74';
  const primaryBtn = { root: { backgroundColor: textColor, color: dark ? '#1e293b' : '#f7f3ee' } };
  const outlineBtn = { root: { borderColor: textColor, color: textColor, backgroundColor: 'transparent' } };
  // Botones pastel apagados (verde/naranja/rojo).
  const pastelBtn = (kind: 'green' | 'orange' | 'red') => {
    const map = {
      green: { bg: dark ? 'rgba(134,184,166,0.18)' : 'rgba(79,138,116,0.14)', fg: dark ? '#86b8a6' : '#4f8a74' },
      orange: { bg: dark ? 'rgba(205,180,135,0.18)' : 'rgba(160,125,60,0.14)', fg: dark ? '#cdb487' : '#a07d3c' },
      red: { bg: dark ? 'rgba(210,155,160,0.18)' : 'rgba(181,86,94,0.14)', fg: dark ? '#d29ba0' : '#b5565e' },
    } as const;
    const c = map[kind];
    return { root: { backgroundColor: c.bg, color: c.fg, border: 'none' } };
  };
  const sectionTitle: React.CSSProperties = { color: textColor, fontFamily: 'Space Grotesk, Inter, sans-serif' };

  // Estado de cupones
  const queryClient = useQueryClient();
  const couponsResult = useQuery(adminDiscountCouponsQuery());
  const coupons = (couponsResult.data as DiscountCouponDTO[] | undefined) ?? [];
  const couponLoading = couponsResult.isFetching;
  const couponError = couponsResult.error ? 'Error al cargar cupones' : null;

  // Estado de creación/edición de cupón
  const [isEditCoupon, setIsEditCoupon] = useState<boolean>(false);
  const [editingCouponId, setEditingCouponId] = useState<number | null>(null);
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponName, setCouponName] = useState<string>('');
  const [couponDiscountType, setCouponDiscountType] = useState<DiscountType>('PERCENTAGE');
  const [couponDiscountValue, setCouponDiscountValue] = useState<number | ''>('');
  const [couponUsageLimit, setCouponUsageLimit] = useState<number | ''>('');
  const [couponExpirationDate, setCouponExpirationDate] = useState<string | null>(null);

  // Modo edición/creación
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editingPlanId, setEditingPlanId] = useState<number | null>(null);

  // Estado de datos reales
  const plansResult = useQuery(adminSubscriptionPlansQuery());
  const summaryResult = useQuery(adminSubscriptionSummaryQuery());
  const plans = (plansResult.data as SubscriptionPlanDTO[] | undefined) ?? [];
  const summary = (summaryResult.data as SubscriptionSummaryDTO | undefined) ?? null;
  const loading = plansResult.isFetching || summaryResult.isFetching;
  const error = plansResult.error || summaryResult.error ? 'Error al cargar datos de suscripciones' : null;

  // Estado del modal de Plan
  const [planName, setPlanName] = useState<string>('');
  const [planPrice, setPlanPrice] = useState<number | ''>('');
  const [planPeriod, setPlanPeriod] = useState<string>('month');
  const [planFeatures, setPlanFeatures] = useState<string>('');
  const [planActive, setPlanActive] = useState<boolean>(true); // se mantiene en UI, no se usa aún en API


  const handleCreatePlan = async () => {
    try {
      if (!planName || planPrice === '' || planPrice == null) {
        notifications.show({ color: 'red', message: 'Nombre y precio son obligatorios' });
        return;
      }
      const billingInterval = {
        'day': 'DAILY',
        'week': 'WEEKLY', 
        'month': 'MONTHLY',
        'quarter': 'QUARTERLY',
        'semester': 'SEMIANNUAL',
        'year': 'YEARLY'
      }[planPeriod] || 'MONTHLY';
      const features = planFeatures
        .split('\n')
        .map((f) => f.trim())
        .filter((f) => f.length > 0);

      const payload: CreateSubscriptionPlanDTO = {
        name: planName,
        price: Number(planPrice),
        currency: 'MXN',
        billingInterval: billingInterval as any,
        features,
      };

      const created = await subscriptionPlanService.createPlan(payload);
      notifications.show({ color: 'green', message: `Plan "${created.name}" creado` });
      setPlanModalOpen(false);
      // limpiar campos
      setPlanName('');
      setPlanPrice('');
      setPlanPeriod('month');
      setPlanFeatures('');
      setPlanActive(true);
      // recargar planes
      queryClient.invalidateQueries({ queryKey: ['adminSubscriptionPlans'] });
    } catch (e: any) {
      console.error(e);
      notifications.show({ color: 'red', message: e?.response?.data?.error || 'Error al crear plan' });
    }
  };

  // Helpers acciones de plan
  const reloadPlans = async () => {
    queryClient.invalidateQueries({ queryKey: ['adminSubscriptionPlans'] });
  };

  // Helpers acciones de cupones
  const reloadCoupons = async () => {
    await queryClient.invalidateQueries({ queryKey: ['adminDiscountCoupons'] });
  };

  const handleOpenCreateCoupon = () => {
    setIsEditCoupon(false);
    setEditingCouponId(null);
    setCouponCode('');
    setCouponName('');
    setCouponDiscountType('PERCENTAGE');
    setCouponDiscountValue('');
    setCouponUsageLimit('');
    setCouponExpirationDate(null);
    setCouponModalOpen(true);
  };

  const handleOpenEditCoupon = (coupon: DiscountCouponDTO) => {
    setIsEditCoupon(true);
    setEditingCouponId(coupon.id);
    setCouponCode(coupon.code || '');
    setCouponName(coupon.name || '');
    setCouponDiscountType((coupon.discountType as DiscountType) || 'PERCENTAGE');
    setCouponDiscountValue(typeof coupon.discountValue === 'string' ? Number(coupon.discountValue) : (coupon.discountValue ?? ''));
    setCouponUsageLimit(coupon.usageLimit ?? '');
    setCouponExpirationDate(coupon.expirationDate || null);
    setCouponModalOpen(true);
  };

  const toLocalDateTime = (d: string | null | undefined): string | undefined => {
    if (!d) return undefined;
    // Si viene como YYYY-MM-DD, el backend espera LocalDateTime => agregar T00:00:00
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return `${d}T00:00:00`;
    return d;
  };

  const handleCreateCoupon = async () => {
    try {
      if (!couponCode || !couponName || couponDiscountValue === '' || couponDiscountValue == null) {
        notifications.show({ color: 'red', message: 'Código, nombre y descuento son obligatorios' });
        return;
      }
      const payload: CreateDiscountCouponDTO = {
        code: couponCode,
        name: couponName,
        discountType: couponDiscountType,
        discountValue: Number(couponDiscountValue),
        usageLimit: couponUsageLimit === '' ? undefined : Number(couponUsageLimit),
        expirationDate: toLocalDateTime(couponExpirationDate),
      };
      const created = await discountCouponService.create(payload);
      notifications.show({ color: 'green', message: `Cupón \"${created.code}\" creado` });
      setCouponModalOpen(false);
      await reloadCoupons();
    } catch (e: any) {
      console.error(e);
      notifications.show({ color: 'red', message: e?.response?.data?.error || 'Error al crear cupón' });
    }
  };

  const handleUpdateCoupon = async () => {
    try {
      if (!editingCouponId) return;
      if (!couponCode || !couponName || couponDiscountValue === '' || couponDiscountValue == null) {
        notifications.show({ color: 'red', message: 'Código, nombre y descuento son obligatorios' });
        return;
      }
      const payload: CreateDiscountCouponDTO = {
        code: couponCode,
        name: couponName,
        discountType: couponDiscountType,
        discountValue: Number(couponDiscountValue),
        usageLimit: couponUsageLimit === '' ? undefined : Number(couponUsageLimit),
        expirationDate: toLocalDateTime(couponExpirationDate),
      };
      const updated = await discountCouponService.update(editingCouponId, payload);
      notifications.show({ color: 'green', message: `Cupón \"${updated.code}\" actualizado` });
      setCouponModalOpen(false);
      setIsEditCoupon(false);
      setEditingCouponId(null);
      await reloadCoupons();
    } catch (e: any) {
      console.error(e);
      notifications.show({ color: 'red', message: e?.response?.data?.error || 'Error al actualizar cupón' });
    }
  };

  const handleDeactivateCoupon = async (id: number) => {
    try {
      await discountCouponService.deactivate(id);
      notifications.show({ color: 'green', message: 'Cupón desactivado' });
      await reloadCoupons();
    } catch (e: any) {
      console.error(e);
      notifications.show({ color: 'red', message: e?.response?.data?.error || 'Error al desactivar cupón' });
    }
  };

  const handleDeleteCoupon = async (id: number) => {
    try {
      await discountCouponService.remove(id);
      notifications.show({ color: 'green', message: 'Cupón eliminado' });
      await reloadCoupons();
    } catch (e: any) {
      console.error(e);
      notifications.show({ color: 'red', message: e?.response?.data?.error || 'Error al eliminar cupón' });
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await subscriptionPlanService.activatePlan(id);
      notifications.show({ color: 'green', message: 'Plan activado' });
      reloadPlans();
    } catch (e: any) {
      notifications.show({ color: 'red', message: e?.response?.data?.error || 'Error al activar' });
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await subscriptionPlanService.deactivatePlan(id);
      notifications.show({ color: 'green', message: 'Plan desactivado' });
      reloadPlans();
    } catch (e: any) {
      notifications.show({ color: 'red', message: e?.response?.data?.error || 'Error al desactivar' });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await subscriptionPlanService.deletePlan(id);
      notifications.show({ color: 'green', message: 'Plan eliminado' });
      reloadPlans();
    } catch (e: any) {
      notifications.show({ color: 'red', message: e?.response?.data?.error || 'Error al eliminar' });
    }
  };

  const handleOpenEdit = (plan: SubscriptionPlanDTO) => {
    setIsEditMode(true);
    setEditingPlanId(plan.id);
    setPlanName(plan.name || '');
    setPlanPrice(Number(plan.price) || 0);
    const period = {
      'DAILY': 'day',
      'WEEKLY': 'week',
      'MONTHLY': 'month', 
      'QUARTERLY': 'quarter',
      'SEMIANNUAL': 'semester',
      'YEARLY': 'year'
    }[plan.billingInterval as string] || 'month';
    setPlanPeriod(period);
    setPlanFeatures((plan.features || []).join('\n'));
    setPlanActive(!!plan.isActive);
    setPlanModalOpen(true);
  };

  const handleUpdatePlan = async () => {
    try {
      if (!editingPlanId) return;
      if (!planName || planPrice === '' || planPrice == null) {
        notifications.show({ color: 'red', message: 'Nombre y precio son obligatorios' });
        return;
      }
      const billingInterval = {
        'day': 'DAILY',
        'week': 'WEEKLY', 
        'month': 'MONTHLY',
        'quarter': 'QUARTERLY',
        'semester': 'SEMIANNUAL',
        'year': 'YEARLY'
      }[planPeriod] || 'MONTHLY';
      const features = planFeatures
        .split('\n')
        .map((f) => f.trim())
        .filter((f) => f.length > 0);

      const payload: CreateSubscriptionPlanDTO = {
        name: planName,
        price: Number(planPrice),
        currency: 'MXN',
        billingInterval: billingInterval as any,
        features,
      };

      const updated = await subscriptionPlanService.updatePlan(editingPlanId, payload);
      notifications.show({ color: 'green', message: `Plan "${updated.name}" actualizado` });
      setPlanModalOpen(false);
      setIsEditMode(false);
      setEditingPlanId(null);
      await reloadPlans();
    } catch (e: any) {
      console.error(e);
      notifications.show({ color: 'red', message: e?.response?.data?.error || 'Error al actualizar plan' });
    }
  };

  // Lista real de cupones cargada desde backend (sin datos mock)

  return (
    <PageTransition type="medical" duration={800}>
      <>
        <Box style={{ flex: 1, padding: '16px 24px', overflowY: 'auto' }}>
            <Stack gap="lg">
              <Group justify="space-between" align="center">
                <AdminPageHeader
                  title="Suscripciones"
                  subtitle="Gestiona planes y cupones de descuento"
                />
                <Group>
                  <Button
                    leftSection={<IconGift size={16} />}
                    variant="default"
                    styles={outlineBtn}
                    onClick={handleOpenCreateCoupon}
                  >
                    Create Coupon
                  </Button>
                  <Button
                    leftSection={<IconPlus size={16} />}
                    styles={primaryBtn}
                    onClick={() => {
                      // Reset para creación
                      setIsEditMode(false);
                      setEditingPlanId(null);
                      setPlanName('');
                      setPlanPrice('');
                      setPlanPeriod('month');
                      setPlanFeatures('');
                      setPlanActive(true);
                      setPlanModalOpen(true);
                    }}
                  >
                    New Plan
                  </Button>
                </Group>
              </Group>


              <Card withBorder radius="lg" p="xl" style={cardStyle}>
                <Title order={4} mb="lg" style={sectionTitle}>
                  Subscription Plans
                </Title>

                <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="lg">
{plans.map((plan) => (
                    <Card key={plan.id} withBorder radius="lg" p="lg" style={{ ...innerCardStyle, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Stack gap="md" style={{ flex: 1 }}>
                        <Group justify="space-between">
                          <Text fw={600} size="lg" style={{ color: textColor }}>
                            {plan.name}
                          </Text>
                          <Text size="xs" fw={600} style={{ color: plan.isActive ? colActiva : mutedColor }}>
                            {plan.isActive ? 'Active' : 'Inactive'}
                          </Text>
                        </Group>

                        <Group align="end" gap="xs">
                          <Text size="2xl" fw={700} style={{ color: textColor }}>
${Number(plan.price)}
                          </Text>
                          <Text size="sm" style={{ color: mutedColor }}>
/{(plan.billingInterval === 'YEARLY' ? 'year' : plan.billingInterval === 'MONTHLY' ? 'month' : plan.billingInterval?.toLowerCase() || 'month')}
                          </Text>
                        </Group>

                        <Stack gap="sm">
                          <Group>
                            <IconUsers size={16} color={mutedColor} />
                            <Text size="sm" style={{ color: mutedColor }}>
{plan.subscribersCount ?? 0} subscribers
                            </Text>
                          </Group>
                          <Group>
                            <IconCoin size={16} color={mutedColor} />
                            <Text size="sm" style={{ color: mutedColor }}>
${Number(plan.totalRevenue ?? 0).toLocaleString()} revenue
                            </Text>
                          </Group>
                        </Stack>

                        <Stack gap="xs">
                          <Text size="sm" fw={600} style={{ color: textColor }}>
                            Features:
                          </Text>
{(plan.features || []).map((feature, index) => (
                            <Text key={index} size="xs" style={{ color: mutedColor }}>
                              • {feature}
                            </Text>
                          ))}
                        </Stack>

<Group gap="xs" mt="auto" pt="md">
                          <Button variant="default" styles={outlineBtn} size="sm" leftSection={<IconEdit size={14} />} fullWidth onClick={() => handleOpenEdit(plan)}>
                            Edit
                          </Button>
                          {plan.isActive ? (
                            <Button variant="light" styles={pastelBtn('orange')} size="sm" leftSection={<IconCircleOff size={14} />} onClick={() => handleDeactivate(plan.id)}>
                              Deactivate
                            </Button>
                          ) : (
                            <Button variant="light" styles={pastelBtn('green')} size="sm" leftSection={<IconPower size={14} />} onClick={() => handleActivate(plan.id)}>
                              Activate
                            </Button>
                          )}
                          <Button variant="light" styles={pastelBtn('red')} size="sm" leftSection={<IconTrash size={14} />} onClick={() => handleDelete(plan.id)}>
                            Delete
                          </Button>
                        </Group>
                      </Stack>
                    </Card>
                  ))}
                </SimpleGrid>
              </Card>

              <Card withBorder radius="lg" p="xl" style={cardStyle}>
                <Title order={4} mb="lg" style={sectionTitle}>
                  Active Coupons
                </Title>

                <Table.ScrollContainer minWidth={500}>
                  <Table highlightOnHover verticalSpacing="sm" withRowBorders style={{ textAlign: 'left' }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Code</Table.Th>
                        <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Discount</Table.Th>
                        <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Usage</Table.Th>
                        <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Expires</Table.Th>
                        <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Status</Table.Th>
                        <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {coupons.map((coupon) => (
                        <Table.Tr key={coupon.id}>
                          <Table.Td>
                            <Text size="sm" fw={600} style={{ color: textColor }}>
                              {coupon.code}
                            </Text>
                            <Text size="xs" style={{ color: mutedColor }}>
                              {coupon.name}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" style={{ color: mutedColor }}>
                              {coupon.discountType === 'PERCENTAGE' ? `${coupon.discountValue}%` : `$${coupon.discountValue}`}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" style={{ color: mutedColor }}>
                              {(coupon.currentUsage ?? 0)}/{coupon.usageLimit ?? '∞'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" style={{ color: mutedColor }}>
                              {coupon.expirationDate ? coupon.expirationDate : '—'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" fw={600} style={{ color: coupon.isActive ? colActiva : mutedColor }}>
                              {coupon.isActive ? 'Active' : 'Inactive'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Menu shadow="md" width={170} position="bottom-end" withinPortal>
                              <Menu.Target>
                                <ActionIcon variant="subtle" color="gray" size="md" aria-label="Acciones">
                                  <IconSettings size={16} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => handleOpenEditCoupon(coupon)}>
                                  Editar
                                </Menu.Item>
                                {coupon.isActive && (
                                  <Menu.Item leftSection={<IconCircleOff size={14} />} onClick={() => handleDeactivateCoupon(coupon.id)}>
                                    Desactivar
                                  </Menu.Item>
                                )}
                                <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => handleDeleteCoupon(coupon.id)}>
                                  Eliminar
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Card>
            </Stack>
        </Box>

        <Modal
          opened={planModalOpen}
          onClose={() => setPlanModalOpen(false)}
          title={isEditMode ? 'Editar Plan' : 'Crear Nuevo Plan'}
          size="lg"
        >
          <Stack gap="md">
<TextInput label="Nombre del Plan" placeholder="Ingresa el nombre del plan" value={planName} onChange={(e) => setPlanName(e.currentTarget.value)} />
            <Group grow>
<NumberInput label="Precio" placeholder="0.00" prefix="$" value={planPrice} onChange={(val) => setPlanPrice(typeof val === 'number' ? val : Number(val) || '' )} />
<Select
                label="Período de Facturación"
                data={[
                  { value: 'day', label: 'Diario' },
                  { value: 'week', label: 'Semanal' },
                  { value: 'month', label: 'Mensual' },
                  { value: 'quarter', label: 'Trimestral (3 meses)' },
                  { value: 'semester', label: 'Semestral (6 meses)' },
                  { value: 'year', label: 'Anual' }
                ]}
                value={planPeriod}
                onChange={(val) => setPlanPeriod(val || 'month')}
              />
            </Group>
<Textarea label="Características" placeholder="Ingresa las características (una por línea)" rows={4} value={planFeatures} onChange={(e) => setPlanFeatures(e.currentTarget.value)} />
<Switch label="Activo" checked={planActive} onChange={(e) => setPlanActive(e.currentTarget.checked)} />
            <Group justify="flex-end">
              <Button variant="light" onClick={() => setPlanModalOpen(false)}>Cancelar</Button>
              {isEditMode ? (
                <Button onClick={handleUpdatePlan}>Guardar Cambios</Button>
              ) : (
                <Button onClick={handleCreatePlan}>Crear Plan</Button>
              )}
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={couponModalOpen}
          onClose={() => setCouponModalOpen(false)}
          title={isEditCoupon ? 'Edit Coupon' : 'Create New Coupon'}
          size="md"
        >
          <Stack gap="md">
            <TextInput label="Coupon Code" placeholder="DISCOUNT20" value={couponCode} onChange={(e) => setCouponCode(e.currentTarget.value.toUpperCase())} />
            <TextInput label="Name" placeholder="Back to school" value={couponName} onChange={(e) => setCouponName(e.currentTarget.value)} />
            <Group grow>
              <Select
                label="Discount Type"
                data={[
                  { value: 'PERCENTAGE', label: 'Percentage (%)' },
                  { value: 'FIXED_AMOUNT', label: 'Fixed ($)' }
                ]}
                value={couponDiscountType}
                onChange={(val) => setCouponDiscountType((val as DiscountType) || 'PERCENTAGE')}
              />
              <NumberInput
                label={couponDiscountType === 'PERCENTAGE' ? 'Discount %' : 'Discount $'}
                placeholder={couponDiscountType === 'PERCENTAGE' ? '20' : '10'}
                max={couponDiscountType === 'PERCENTAGE' ? 100 : undefined}
                min={0}
                value={couponDiscountValue}
                onChange={(val) => setCouponDiscountValue(typeof val === 'number' ? val : Number(val) || '')}
              />
            </Group>
            <Group grow>
              <NumberInput label="Usage limit" placeholder="100" value={couponUsageLimit} onChange={(val) => setCouponUsageLimit(typeof val === 'number' ? val : Number(val) || '')} />
              <TextInput
                label="Expires At"
                type="date"
                placeholder="YYYY-MM-DD"
                value={couponExpirationDate ?? ''}
                onChange={(e) => setCouponExpirationDate(e.currentTarget.value || null)}
                size="sm"
                radius="md"
              />
            </Group>
            <Group justify="flex-end">
              <Button variant="light" onClick={() => setCouponModalOpen(false)}>Cancel</Button>
              {isEditCoupon ? (
                <Button onClick={handleUpdateCoupon}>Save Changes</Button>
              ) : (
                <Button onClick={handleCreateCoupon}>Create Coupon</Button>
              )}
            </Group>
          </Stack>
        </Modal>
      </>
    </PageTransition>
  );
};

export default SubscriptionsPage;